/* The Audio page's dropdown: the console's own styled menu instead of the browser's select.
   The menu is portalled to the page root at fixed coordinates, so the account list Card it
   sits in never clips it, and opens upward when there is no room below. Long lists (accounts)
   get a filter box. */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../components/ChatAssistant/compact/kit'
import { C } from '../components/ChatAssistant/compact/tokens'

export interface PickerOption {
  id: string
  label: string
  /** One muted line under the label (e.g. "@username"). */
  sub?: string
}

const FILTER_FROM = 8

export function Picker({ value, options, onChange, placeholder, label, disabled, filterPlaceholder, wide }: {
  value: string
  options: PickerOption[]
  onChange: (id: string) => void
  placeholder?: string
  /** Accessible name of the trigger. */
  label: string
  disabled?: boolean
  filterPlaceholder?: string
  /** Fill the width of its parent instead of the row width. */
  wide?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [at, setAt] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLDivElement>(null)
  const cur = options.find((o) => o.id === value)

  const place = () => {
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    const below = window.innerHeight - r.bottom - 12
    const above = r.top - 12
    const up = below < 220 && above > below
    const maxHeight = Math.min(320, Math.max(140, up ? above : below))
    const width = Math.min(Math.max(r.width, 220), window.innerWidth - 16)
    setAt({ top: up ? r.top - 4 - maxHeight : r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)), width, maxHeight })
  }

  useLayoutEffect(() => {
    if (!open) return
    setQuery('')
    place()
  }, [open])

  useEffect(() => {
    if (!open) return
    const outside = (e: MouseEvent) => {
      const t = e.target as Node
      if (!menu.current?.contains(t) && !trigger.current?.contains(t)) setOpen(false)
    }
    const follow = (e: Event) => !menu.current?.contains(e.target as Node) && place()
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      setOpen(false)
      trigger.current?.focus()
    }
    const close = () => setOpen(false)
    window.addEventListener('mousedown', outside)
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', close)
    window.addEventListener('keydown', key, true)
    return () => {
      window.removeEventListener('mousedown', outside)
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', close)
      window.removeEventListener('keydown', key, true)
    }
  }, [open])

  const q = query.trim().toLocaleLowerCase()
  const shown = q ? options.filter((o) => `${o.label} ${o.sub ?? ''}`.toLocaleLowerCase().includes(q)) : options
  const pick = (id: string) => {
    setOpen(false)
    if (id !== value) onChange(id)
  }

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className={`vo-picker ${open ? 'is-open' : ''} ${wide ? 'is-wide' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`vo-picker-label ${cur ? '' : 'is-placeholder'}`} title={cur?.label}>
          {cur?.label ?? placeholder}
        </span>
        <Icon name="chevron-down" size={13} color={C.sub} />
      </button>
      {open &&
        at &&
        createPortal(
          <div ref={menu} className="vo-picker-menu" style={{ top: at.top, left: at.left, width: at.width, maxHeight: at.maxHeight }}>
            {options.length >= FILTER_FROM && (
              <input className="vo-picker-filter" autoFocus autoComplete="off" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={filterPlaceholder} aria-label={filterPlaceholder} />
            )}
            <div className="vo-picker-scroll" role="listbox" aria-label={label}>
              {!shown.length && <div className="vo-picker-empty">—</div>}
              {shown.map((o) => {
                const on = o.id === value
                return (
                  <button key={o.id} type="button" role="option" aria-selected={on} className={`vo-picker-item ${on ? 'is-on' : ''}`} onClick={() => pick(o.id)}>
                    <span className="vo-picker-text">
                      <span className="vo-picker-name">{o.label}</span>
                      {o.sub && <span className="vo-picker-sub">{o.sub}</span>}
                    </span>
                    <Icon name="check" size={14} color={C.purpleLt} style={{ opacity: on ? 1 : 0 }} />
                  </button>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
